import { padSegment } from '@/utils/priceListProductCalculator';
import { padSubchapterCode } from '@/data/productStructureLookups';

/**
 * @param {string} productCode e.g. 101.02.51
 * @returns {{ chapterCode: string, subchapterCode: string, progressiveNumber: string }|null}
 */
export function parseProductCodeStructure(productCode) {
    const parts = String(productCode || '').trim().split('.');
    if (parts.length < 3) {
        return null;
    }
    const chapterPart = parts[0];
    if (chapterPart.length < 3) {
        return null;
    }
    return {
        chapterCode: chapterPart.slice(0, -2),
        subchapterCode: chapterPart.slice(-2),
        progressiveNumber: padSegment(parts[1], 2),
        manufacturerCode: String(parts[2] ?? '').trim(),
    };
}

/**
 * @param {Array<string>} existingProductCodes
 * @param {string} chapterCode
 * @param {string} subchapterCode
 * @returns {string}
 */
export function suggestNextProgressiveNumber(existingProductCodes, chapterCode, subchapterCode) {
    const subchapter = padSubchapterCode(subchapterCode);
    const chapterPart = `${String(chapterCode || '').trim()}${padSubchapterCode(subchapter)}`;
    if (!chapterPart || !subchapter) {
        return '';
    }

    let maxProgressive = 0;
    for (const productCode of existingProductCodes || []) {
        const parsed = parseProductCodeStructure(productCode);
        if (!parsed) {
            continue;
        }
        const candidateChapterPart = `${parsed.chapterCode}${parsed.subchapterCode}`;
        if (candidateChapterPart !== chapterPart) {
            continue;
        }
        const progressive = Number.parseInt(parsed.progressiveNumber, 10);
        if (Number.isFinite(progressive)) {
            maxProgressive = Math.max(maxProgressive, progressive);
        }
    }

    return padSegment(maxProgressive + 1, 2);
}

/**
 * Keeps the higher of two suggested progressive numbers ("16" vs "03" → "16").
 * @param {unknown} left
 * @param {unknown} right
 * @returns {string}
 */
export function pickHigherProgressiveNumber(left, right) {
    const leftValue = Number.parseInt(String(left ?? '').trim(), 10);
    const rightValue = Number.parseInt(String(right ?? '').trim(), 10);
    const leftOk = Number.isFinite(leftValue);
    const rightOk = Number.isFinite(rightValue);
    if (!leftOk && !rightOk) {
        return '';
    }
    if (!leftOk) {
        return padSegment(rightValue, 2);
    }
    if (!rightOk) {
        return padSegment(leftValue, 2);
    }
    return padSegment(Math.max(leftValue, rightValue), 2);
}

/**
 * @param {Array<{ product_code?: string, price?: number|string }>} existingProducts
 * @param {string} productCode
 * @returns {string}
 */
export function findReferencePriceForProduct(existingProducts, productCode) {
    const normalizedCode = String(productCode || '').trim();
    if (!normalizedCode) {
        return '';
    }
    const match = (existingProducts || []).find(
        (product) => String(product.product_code || '').trim() === normalizedCode
    );
    if (!match || match.price === null || match.price === undefined || match.price === '') {
        return '';
    }
    return String(match.price);
}

/**
 * Suggests the next progressive numeric catalog code (manufacturers / furnizori).
 * Returns an unpadded decimal string (1, 2, … 10, 11, …).
 * Non-numeric codes in the list are ignored.
 * @param {Array<{ code?: string|number }>} rows
 * @returns {string}
 */
export function suggestNextCatalogCode(rows) {
    let maxNumeric = -1;
    for (const row of rows || []) {
        const code = String(row?.code ?? '').trim();
        if (!/^\d+$/.test(code)) {
            continue;
        }
        const value = Number.parseInt(code, 10);
        if (Number.isFinite(value)) {
            maxNumeric = Math.max(maxNumeric, value);
        }
    }
    return String(maxNumeric + 1);
}

/**
 * @param {Array<{ name: string, code: string }>} manufacturers
 * @param {string} code
 * @returns {{ name: string, code: string }|undefined}
 */
export function findManufacturerByCode(manufacturers, code) {
    const normalized = String(code ?? '').trim();
    if (!normalized) {
        return undefined;
    }
    return (manufacturers || []).find(
        (manufacturer) => String(manufacturer.code).trim() === normalized
    );
}
